import React, { memo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useIsMobile } from '@/hooks/use-mobile'
import { PanelBody } from './root-panel/components/panel-body'
import { PanelRail } from './root-panel/components/panel-rail'
import { PanelShell } from './root-panel/components/panel-shell'
import { StackedPanel } from './root-panel/components/stacked-panel'
import { RIGHT_PANEL_IDS } from './root-panel/constants/panel-ids'
import { useBackgroundsSettings } from './root-panel/hooks/use-backgrounds-settings'
import { useClikArtFilters } from './root-panel/hooks/use-clikart-filters'
import { useEscapeClosesPanel } from './root-panel/hooks/use-escape-closes-panel'
import { usePhotosEscapeChain } from './root-panel/hooks/use-photos-escape-chain'
import { usePortraitsFilters } from './root-panel/hooks/use-portraits-filters'
import { useRightPanelState } from './root-panel/hooks/use-right-panel-state'
import { useRightPanelWindowEvents } from './root-panel/hooks/use-right-panel-window-events'
import { useTemplatesFilters } from './root-panel/hooks/use-templates-filters'

type RightPanelProps = {
  onPanelActivate?: () => void
  /** Called whenever a right-side panel actually opens or switches to a different one (never on
   *  close) — lets the host reset the left Properties Panel / active tool if needed. This
   *  component never touches that state itself; see `LeftPanelRef.handleRightPanelOpened`. */
  onPanelOpened?: () => void
  spreadId?: string | null
  /** Optional ref on the whole right panel — its left edge is wherever the panel currently
   *  starts: the open panel's content when expanded, the icon rail when collapsed. Used to align
   *  overlays such as the zoom preset menu so they never sit on top of an open panel. */
  edgeRef?: React.RefObject<HTMLDivElement | null>
  /** Host element below the spread area that the open panel is portaled into on narrow viewports,
   *  so the spread and the panel stack vertically (WMPCM-11050 AC-1). Without one, the stacked
   *  panel renders in place. */
  stackSlotRef?: React.RefObject<HTMLDivElement | null>
  /** Called when the Escape-key priority chain closes a panel as its last resort (nothing else
   *  open) — the host should fall back to the default Select tool (WMPCM-10936 AC-5). Does not
   *  touch focus itself; this component moves focus back to its own rail icon. */
  onEscapeFallbackToSelectTool?: () => void
  /** WMPCM-11088 — when Photos is open, Escape deselects a selected canvas photo and collapses the
   *  left Properties panel before closing dropdowns / the Photos panel itself. Return true if handled. */
  onEscapeClearPhotoSelectionAndCloseLeftPanel?: () => boolean
  /** Called after a `requestRightPanelClose` from outside the rail (the menu bar's Previous/Next
   *  spread buttons) actually closed the showing panel — the host falls back to the Select tool. */
  onClosedByRequest?: () => void
}

/** Header labels */
function resolvePanelTitle(activePanel: string | null): string {
  if (!activePanel) return ''
  if (activePanel === RIGHT_PANEL_IDS.photos) return 'Photos'
  if (activePanel === RIGHT_PANEL_IDS.clikart) return 'ClikArt'
  if (activePanel === RIGHT_PANEL_IDS.adorders) return 'Ad Orders'

  // Some ids carry a trailing space (see `RIGHT_PANEL_IDS`) — never part of the title.
  const label = activePanel.trim()

  return label.charAt(0).toUpperCase() + label.slice(1)
}

function RightPanel({
  onPanelActivate,
  onPanelOpened,
  spreadId = null,
  edgeRef,
  stackSlotRef,
  onEscapeFallbackToSelectTool,
  onEscapeClearPhotoSelectionAndCloseLeftPanel,
  onClosedByRequest,
}: RightPanelProps = {}) {
  // Hook call order below is load-bearing: it keeps every effect in its original relative
  // order (useIsMobile → ClikArt/Backgrounds/Templates/Portraits/Photos Escape chains → window
  // events).
  const isMobile = useIsMobile()
  const { isOpen, setIsOpen, activePanel, setActivePanel, closePanel, selectPanel, toggleOpen } =
    useRightPanelState({ isMobile, onPanelActivate, onPanelOpened })
  const clikArtFilters = useClikArtFilters()
  const templatesFilters = useTemplatesFilters()
  const portraitsFilters = usePortraitsFilters()

  // Rail icon buttons, keyed by panel — used to return keyboard focus to the right icon
  // (e.g. the ClikArt icon specifically) when the Escape chain closes a panel (AC-5).
  const railButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const backgrounds = useBackgroundsSettings(spreadId)

  const escapeChain = {
    isOpen,
    activePanel,
    closePanel,
    railButtonRefs,
    onEscapeFallbackToSelectTool,
  }

  // ClikArt — priority per press: hover preview / dropdowns (Radix, reported through
  // `onAnyDropdownOpenChange`), then a selected placed ClikArt on the canvas (deselect + collapse
  // the left Properties panel, US-19), then the panel itself. A placed ClikArt is a `photoFill`
  // shape, so the Photos selection check covers it.
  const handleClikArtDropdownOpenChange = useEscapeClosesPanel({
    panelId: RIGHT_PANEL_IDS.clikart,
    ...escapeChain,
    onEscapeClearSelection: onEscapeClearPhotoSelectionAndCloseLeftPanel,
  })

  // Backgrounds — a background thumbnail's hover/focus preview is also a Radix Popover and closes
  // the same way as a dropdown (see `BackgroundThumbnail`'s `onEscapeKeyDown`). Deliberately NOT
  // implemented here yet (no feature exists to escalate from): a selected background on the canvas
  // closing the Left Panel per US-19 — background placement in this codebase is session-only
  // drag-and-drop with no on-canvas selection state to close from yet. When it lands, insert its
  // check above the dropdown check, in priority order.
  const handleBackgroundsDropdownOpenChange = useEscapeClosesPanel({
    panelId: RIGHT_PANEL_IDS.backgrounds,
    ...escapeChain,
  })

  // Templates — a template thumbnail's hover/focus preview is also a Radix Popover and closes the
  // same way (see `TrayThumbnailCard`'s `onEscapeKeyDown`). Deliberately NOT implemented here yet
  // (no feature exists to escalate from): a selected template instance on the canvas closing the
  // Left Panel per US-13 — template placement in this codebase is browse/preview-only today with
  // no on-canvas selection state to close from yet (`TemplateThumbnail` explicitly has no
  // apply/drag interaction). Same documented exclusion as Backgrounds, for the same reason. When
  // it lands, insert its check above the dropdown check, in priority order.
  const handleTemplatesDropdownOpenChange = useEscapeClosesPanel({
    panelId: RIGHT_PANEL_IDS.templates,
    ...escapeChain,
  })

  // Portraits — no feature exists to escalate from yet (no selectable placed portrait on the
  // canvas, no hover preview), so Escape is: open dropdown first, then the panel itself. When
  // placement lands, pass `onEscapeClearSelection` here like ClikArt does, and add the hover
  // preview to the panel's `onAnyDropdownOpenChange` report.
  const handlePortraitsDropdownOpenChange = useEscapeClosesPanel({
    panelId: RIGHT_PANEL_IDS.portraits,
    ...escapeChain,
  })

  const { handlePhotosDropdownOpenChange, photosEscapeStateRef } = usePhotosEscapeChain({
    isOpen,
    activePanel,
    closePanel,
    railButtonRefs,
    onEscapeClearPhotoSelectionAndCloseLeftPanel,
    onEscapeFallbackToSelectTool,
  })

  useRightPanelWindowEvents({
    isMobile,
    isOpen,
    activePanel,
    setIsOpen,
    setActivePanel,
    closePanel,
    onPanelActivate,
    onPanelOpened,
    onClosedByRequest,
  })

  const headerTitle = resolvePanelTitle(activePanel)
  // Read at render time: the slot sits earlier in the host's tree, so it is attached long before
  // the panel can first open.
  const stackSlot = stackSlotRef?.current ?? null

  const body = (
    <PanelBody
      activePanel={activePanel}
      spreadId={spreadId}
      onPhotosDropdownOpenChange={handlePhotosDropdownOpenChange}
      photosEscapeStateRef={photosEscapeStateRef}
      clikArtFilters={clikArtFilters}
      onClikArtDropdownOpenChange={handleClikArtDropdownOpenChange}
      templatesFilters={templatesFilters}
      // Follow-up (pre-existing bug, kept on purpose): a new arrow every render, unlike the other
      // panels' stable callbacks. `TemplatesPanel` reports "no dropdown open" from a cleanup keyed
      // on this prop, so any re-render while a Templates dropdown is open clears the flag and the
      // next Escape closes the whole panel. Pass `handleTemplatesDropdownOpenChange` directly to fix.
      onTemplatesDropdownOpenChange={(open) => handleTemplatesDropdownOpenChange(open)}
      backgroundsSettings={backgrounds.settings}
      onBackgroundsSettingsChange={backgrounds.onSettingsChange}
      onBackgroundsDropdownOpenChange={handleBackgroundsDropdownOpenChange}
      portraitsFilters={portraitsFilters}
      onPortraitsDropdownOpenChange={handlePortraitsDropdownOpenChange}
    />
  )

  return (
    <div
      ref={edgeRef as React.RefObject<HTMLDivElement>}
      className="flex h-full overflow-hidden"
      data-right-panel-root="true"
    >
      {/* Right panel */}
      {!isMobile && isOpen && (
        <PanelShell
          title={headerTitle}
          fullWidthHeaderBorder={
            activePanel === RIGHT_PANEL_IDS.photos ||
            activePanel === RIGHT_PANEL_IDS.backgrounds ||
            activePanel === RIGHT_PANEL_IDS.templates ||
            activePanel === RIGHT_PANEL_IDS.portraits
          }
        >
          {body}
        </PanelShell>
      )}

      {/* Rail - always visible */}
      <PanelRail
        isOpen={isOpen}
        activePanel={activePanel}
        spreadId={spreadId}
        railButtonRefs={railButtonRefs}
        onToggle={toggleOpen}
        onSelectPanel={selectPanel}
      />

      {/* Narrow viewports: stacked under the spread area instead of beside it */}
      {isMobile &&
        isOpen &&
        (stackSlot ? (
          createPortal(<StackedPanel title={headerTitle}>{body}</StackedPanel>, stackSlot)
        ) : (
          <StackedPanel title={headerTitle}>{body}</StackedPanel>
        ))}
    </div>
  )
}

/** Memoized: home re-renders while a selection moves, and nothing here depends on that. */
export default memo(RightPanel)
